import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CharacterCard } from '../types';
import { processFirestoreCardsLight } from '../utils/cardUtils';
import { sortCardsForCalendar } from '../utils/cardDateUtils';
import { OWNER_UID } from '../constants/owner';
import { useTheme } from '../contexts/ThemeContext';
import Header from './Header';
import Card from './Card';
import SkeletonCard from './SkeletonCard';
import { navigate } from './Router';

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'with', 'and', 'or', 'is', 'are',
  'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will',
  'would', 'could', 'should', 'may', 'might', 'can', 'shall', 'it', 'its', 'this', 'that',
  'these', 'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'she', 'they', 'them',
  'from', 'by', 'as', 'into', 'about', 'between', 'through', 'during', 'before', 'after',
  'above', 'below', 'up', 'down', 'out', 'off', 'over', 'under', 'no', 'not', 'very', 'just',
  'small', 'large', 'big', 'little', 'old', 'new', 'young', 'long', 'short', 'high', 'low',
]);

export const extractWords = (phrase: string): string[] => {
  return phrase.toLowerCase()
    .split(/[\s,.\-\/]+/)
    .filter(w => w.length > 1 && !STOP_WORDS.has(w));
};

// 上位タグからランダムに n 個選ぶ
const pickRandom = <T,>(arr: T[], n: number): T[] => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
};

interface TagPageProps {
  tagName: string;
}

const TagPage: React.FC<TagPageProps> = ({ tagName }) => {
  const [allCards, setAllCards] = useState<CharacterCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allTags, setAllTags] = useState<{ name: string; count: number }[]>([]);
  const [relatedTags, setRelatedTags] = useState<{ name: string; count: number }[]>([]);
  const [showcaseTags, setShowcaseTags] = useState<{ name: string; count: number }[]>([]);
  const { theme } = useTheme();

  // タグ → カードのマッピング
  const tagCardsMap = useMemo(() => {
    const map = new Map<string, CharacterCard[]>();
    allCards.forEach(card => {
      const wordsSeen = new Set<string>();
      card.characterData?.mainObjects?.forEach(obj => {
        extractWords(obj).forEach(word => {
          if (!wordsSeen.has(word)) {
            wordsSeen.add(word);
            if (!map.has(word)) map.set(word, []);
            map.get(word)!.push(card);
          }
        });
      });
    });
    return map;
  }, [allCards]);

  // tagCardsMapから導出（一貫した数値を保証）
  const filteredCards = useMemo(() => {
    if (!tagName) return [];
    const cards = tagCardsMap.get(tagName.toLowerCase()) || [];
    return sortCardsForCalendar(cards);
  }, [tagName, tagCardsMap]);

  useEffect(() => {
    const fetchCards = async () => {
      if (!db) { setIsLoading(false); return; }

      try {
        const cardsQuery = query(
          collection(db, 'cards'),
          where('userId', '==', OWNER_UID),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(cardsQuery);
        const cards = processFirestoreCardsLight(snapshot.docs, { includePrivate: true });
        setAllCards(cards);

        // 単語ベースでタグ一覧を生成
        const tagMap = new Map<string, number>();
        cards.forEach(card => {
          const wordsSeen = new Set<string>();
          card.characterData?.mainObjects?.forEach(obj => {
            extractWords(obj).forEach(word => {
              if (!wordsSeen.has(word)) {
                wordsSeen.add(word);
                tagMap.set(word, (tagMap.get(word) || 0) + 1);
              }
            });
          });
        });
        const tags = Array.from(tagMap.entries())
          .filter(([, count]) => count >= 2)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setAllTags(tags);

        // 上位20タグからランダム10個をショーケース用に選択
        const topTags = tags.slice(0, Math.min(20, tags.length));
        setShowcaseTags(pickRandom(topTags, 10));

        // 個別タグの場合は関連タグ計算
        if (tagName) {
          const targetTag = tagName.toLowerCase();

          // 共起タグを計算（同じカードに出てくるタグ）
          const filtered = cards.filter(card =>
            card.characterData?.mainObjects?.some(obj =>
              extractWords(obj).includes(targetTag)
            )
          );
          const cooccurrence = new Map<string, number>();
          filtered.forEach(card => {
            const words = new Set<string>();
            card.characterData?.mainObjects?.forEach(obj => {
              extractWords(obj).forEach(w => words.add(w));
            });
            words.delete(targetTag);
            words.forEach(w => {
              cooccurrence.set(w, (cooccurrence.get(w) || 0) + 1);
            });
          });
          const related = Array.from(cooccurrence.entries())
            .filter(([name]) => tags.some(t => t.name === name))
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 20);
          setRelatedTags(related);
        }
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, [tagName]);

  return (
    <div className="min-h-screen p-4 sm:p-8 font-sans flex flex-col bg-theme-primary">
      <Header />

      <main className="flex-grow">
        {/* ヘッダー */}
        <div className="mb-4">
          {tagName ? (
            <div className="flex items-center -mr-4 sm:-mr-8">
              <div className="flex-shrink-0 flex items-center space-x-2 mr-3">
                <button
                  onClick={() => navigate('/tags')}
                  className="text-theme-tertiary hover:text-brand-primary transition-colors text-sm"
                >
                  tags
                </button>
                <span className="text-theme-tertiary">/</span>
                <h2 className="text-xl fira-sans-extra-bold-italic text-theme-primary whitespace-nowrap">
                  #{tagName}
                  <span className="text-sm text-theme-tertiary font-normal ml-2">
                    {filteredCards.length || ''}
                  </span>
                </h2>
              </div>

              {/* 関連タグ（共起ベース） */}
              {relatedTags.length > 0 && (
                <div className="flex overflow-x-auto scrollbar-hide space-x-2 pr-4 sm:pr-8">
                  {relatedTags.map(tag => (
                    <button
                      key={tag.name}
                      onClick={() => navigate(`/tags/${encodeURIComponent(tag.name)}`)}
                      className="flex-shrink-0 px-3 py-1.5 text-sm rounded-full bg-theme-secondary text-theme-secondary hover:bg-theme-tertiary transition-colors"
                    >
                      {tag.name} <span className="text-xs opacity-70">{(tagCardsMap.get(tag.name) || []).length}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('/')}
                className="text-theme-tertiary hover:text-brand-primary transition-colors text-sm"
              >
                home
              </button>
              <span className="text-theme-tertiary">/</span>
              <h2 className="text-xl fira-sans-extra-bold-italic text-theme-primary">
                tags
              </h2>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={`skeleton-${i}`} />
            ))}
          </div>
        ) : tagName ? (
          /* 個別タグページ: カードグリッド */
          filteredCards.length === 0 ? (
            <div className="flex justify-center py-16">
              <div className="text-theme-tertiary">No cards found with tag "#{tagName}"</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
              {filteredCards.map(card => (
                <Card
                  key={card.id}
                  card={card}
                  onGenerate={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </div>
          )
        ) : (
          /* タグ一覧ページ: ショーケース + 残りタグ */
          <div className="space-y-6">
            {/* ショーケース: ランダム10タグ */}
            {showcaseTags.map(tag => {
              const tagCards = tagCardsMap.get(tag.name) || [];
              return (
                <div key={tag.name} className="sm:mx-0 -mx-4">
                  <div className="flex gap-3 overflow-x-auto sm:overflow-hidden scrollbar-hide px-4 sm:px-0">
                    {/* タグ名カード */}
                    <button
                      onClick={() => navigate(`/tags/${encodeURIComponent(tag.name)}`)}
                      className={`flex-shrink-0 w-36 sm:w-[calc((100%-3*0.75rem)/4)] md:w-[calc((100%-4*0.75rem)/5)] lg:w-[calc((100%-4*0.75rem)/5)] xl:w-[calc((100%-5*0.75rem)/6)] aspect-[3/4] rounded-lg ${theme === 'dark' ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-theme-secondary hover:bg-theme-tertiary'} relative transition-colors overflow-hidden`}
                    >
                      <div className="absolute top-4 left-4 right-2 sm:right-auto overflow-hidden text-left">
                        <div className={`text-2xl fira-sans-extra-bold-italic leading-tight break-all sm:break-normal ${
                          theme === 'dark' ? 'text-zinc-400' : ''
                        }`} style={theme === 'light' ? { color: '#B3A5A9' } : {}}>
                          #{tag.name}
                        </div>
                        <div className={`text-sm fira-sans-book-italic mt-1 ${
                          theme === 'dark' ? 'text-zinc-500' : ''
                        }`} style={theme === 'light' ? { color: '#B3A5A9' } : {}}>
                          {tagCards.length} cards
                        </div>
                      </div>
                    </button>

                    {/* タグに属するカード（グリッドの1行に収まる分だけ表示） */}
                    {tagCards.slice(0, 5).map(card => (
                      <div key={card.id} className="flex-shrink-0 w-40 sm:w-[calc((100%-3*0.75rem)/4)] md:w-[calc((100%-4*0.75rem)/5)] lg:w-[calc((100%-4*0.75rem)/5)] xl:w-[calc((100%-5*0.75rem)/6)]">
                        <Card
                          card={card}
                          onGenerate={() => {}}
                          onDelete={() => {}}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* 残りのタグ一覧 */}
            {allTags.length > 0 && (
              <div>
                <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${
                  theme === 'dark' ? 'text-zinc-500' : 'text-theme-tertiary'
                }`}>
                  All Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <button
                      key={tag.name}
                      onClick={() => navigate(`/tags/${encodeURIComponent(tag.name)}`)}
                      className="px-3 py-1.5 text-sm rounded-full bg-theme-secondary text-theme-secondary hover:bg-brand-primary hover:text-white transition-colors"
                    >
                      #{tag.name} <span className="text-xs opacity-70">{(tagCardsMap.get(tag.name) || []).length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TagPage;
